// Org default API keys — Settings ApiKeysTab UX (row expand / test / clear) wired to org endpoints.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@/hooks/useToast';
import { useConfirm } from '@/hooks/useConfirm';
import { useModal } from '@/hooks/useModal';
import { Button, Modal } from '@/components/ui';
import { organizationService } from '@/services/api';
import { KEYS, KEY_GROUPS, USER_EDITABLE_KEYS } from '@/features/apiKeys/catalog';
import type { ApiKeyTestResult, OrgKeyName } from '@/types';
import type { EditData, EditPayload } from '@/services/api/organizationService';
import OrgSectionHeading from './OrgSectionHeading';
import '@/styles/pages/settings.css';

const APOLLO_ENDPOINTS = [
  { endpoint: '/api/v1/organizations/search', description: 'Required for Apollo Search.' },
  {
    endpoint: '/api/v1/organizations/enrich',
    description: 'Required for single company enrichment.',
  },
  { endpoint: '/api/v1/organizations/bulk_enrich', description: 'Required for bulk enrichment.' },
  {
    endpoint: '/api/v1/mixed_people/api_search',
    description: 'Required for contact/investor search.',
  },
  { endpoint: '/api/v1/people/match', description: 'Required for email matching/unlocking.' },
];

export interface OrgApiKeysSectionProps {
  slug: string;
  data: EditData;
  onKeyStatusChange: (next: Record<OrgKeyName, boolean>) => void;
  profileSnapshot: {
    name: string;
    allowMemberKeys: boolean;
    orgKeyFallback: boolean;
    membersSeeOrgUsage: boolean;
  };
}

export default function OrgApiKeysSection({
  slug,
  data,
  onKeyStatusChange,
  profileSnapshot,
}: OrgApiKeysSectionProps) {
  const { success, error, info } = useToast();
  const confirm = useConfirm();
  const apolloModal = useModal();

  const [results, setResults] = useState<Partial<Record<OrgKeyName, ApiKeyTestResult>>>({});
  const [busy, setBusy] = useState<'update' | 'test' | 'clear' | null>(null);
  const [expandedKey, setExpandedKey] = useState<OrgKeyName | null>(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (expandedKey) inputRef.current?.focus();
  }, [expandedKey]);

  const configured = useMemo(
    () => data.keyStatus ?? ({} as Record<OrgKeyName, boolean>),
    [data.keyStatus],
  );

  const profilePayload = (): Omit<EditPayload, 'keys'> => ({
    name: profileSnapshot.name.trim(),
    allow_member_keys: profileSnapshot.allowMemberKeys,
    org_key_fallback: profileSnapshot.orgKeyFallback,
    members_see_org_usage: profileSnapshot.membersSeeOrgUsage,
  });

  const runTest = async () => {
    setBusy('test');
    try {
      const res = await organizationService.testApiKeys(slug);
      if (res.success) {
        setResults(res.testResults);
        const resultsMap = res.testResults;
        const keys = (Object.keys(resultsMap) as OrgKeyName[]).filter((k) =>
          USER_EDITABLE_KEYS.has(k),
        );
        const valid = keys.filter((k) => resultsMap[k]?.status === 'valid').length;
        const formatLine = (k: OrgKeyName) => {
          const name = k
            .replace(/_/g, ' ')
            .replace(/api key/i, '')
            .trim();
          const capName = name.charAt(0).toUpperCase() + name.slice(1);
          const r = resultsMap[k];
          const icon =
            r?.status === 'valid'
              ? '✅'
              : ['rate_limited', 'service_unavailable'].includes(r?.status || '')
                ? '⏳'
                : '❌';
          return `${icon} ${capName}: ${r?.message || 'Not tested'}`;
        };
        let summary = `${valid}/${keys.length} API keys are working properly.\n\n`;
        keys.forEach((k) => {
          summary += formatLine(k) + '\n';
        });
        success('API Keys Test Complete', summary, false);
      } else {
        error('API Keys Test Failed', res.message || 'Unknown error occurred');
      }
    } catch {
      error('API Keys Test Error', 'Failed to test API keys. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const testSingleKey = async (keyName: OrgKeyName, keyValue?: string) => {
    if (!USER_EDITABLE_KEYS.has(keyName)) return;
    setBusy('test');
    try {
      const res = await organizationService.testSingleApiKey(slug, keyName, keyValue);
      if (res.success) {
        if (!keyValue) {
          setResults((prev) => ({ ...prev, [keyName]: res.testResults[keyName] }));
        }
        const row = res.testResults[keyName];
        if (row.status === 'valid') {
          success('Test Successful', row.message);
        } else if (
          ['credits_exhausted', 'model_unavailable', 'quota_exceeded', 'rate_limited'].includes(
            row.status,
          )
        ) {
          info('Test Warning', row.message);
        } else {
          error('Test Failed', row.message);
        }
      } else {
        error('Test Failed', res.message || 'Unknown error occurred');
      }
    } catch {
      error('Test Error', 'Failed to test API key. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const updateKey = async (keyName: OrgKeyName) => {
    if (!USER_EDITABLE_KEYS.has(keyName)) return;
    const v = inputValue.trim();
    if (!v) {
      error('Empty API Key', 'Please enter a valid API key to save.');
      return;
    }
    setBusy('update');
    try {
      const result = await organizationService.saveEdit(slug, {
        ...profilePayload(),
        keys: { [keyName]: v },
      });
      if (result.success) {
        onKeyStatusChange({ ...configured, [keyName]: true });
        setResults((r) => {
          const next = { ...r };
          delete next[keyName];
          return next;
        });
        success('API Key Saved', result.message || 'Organization API key updated.');
        setExpandedKey(null);
        setInputValue('');
        // Only verify the key that was just saved — full suite is "Test All API Keys".
        setTimeout(() => void testSingleKey(keyName), 600);
      } else {
        error('Update Failed', result.message || 'Failed to update API key.');
      }
    } catch {
      error('Error', 'An error occurred while updating the API key.');
    } finally {
      setBusy(null);
    }
  };

  const clearKey = async (keyName: OrgKeyName) => {
    if (!USER_EDITABLE_KEYS.has(keyName)) return;
    const ok = await confirm({
      title: 'Clear API Key',
      message: 'Are you sure you want to clear this organization API key?',
      confirmText: 'Clear Key',
      cancelText: 'Cancel',
    });
    if (!ok) return;

    setBusy('clear');
    try {
      const result = await organizationService.saveEdit(slug, {
        ...profilePayload(),
        keys: { [keyName]: '' },
      });
      if (result.success) {
        onKeyStatusChange({ ...configured, [keyName]: false });
        setResults((r) => {
          const next = { ...r };
          delete next[keyName];
          return next;
        });
        success('API Key Cleared', result.message || 'The organization API key has been removed.');
      } else {
        error('Clear Failed', result.message || 'Failed to clear API key.');
      }
    } catch {
      error('Error', 'An error occurred while clearing API key.');
    } finally {
      setBusy(null);
    }
  };

  const clearAllKeys = async () => {
    const ok = await confirm({
      title: 'Clear Data Source Keys',
      message:
        'Are you sure you want to clear all organization Data Source API keys? This cannot be undone.',
      confirmText: 'Clear Keys',
      cancelText: 'Cancel',
    });
    if (!ok) return;

    setBusy('clear');
    try {
      const result = await organizationService.clearApiKeys(slug);
      if (result.success) {
        const nextStatus = { ...configured };
        USER_EDITABLE_KEYS.forEach((k) => {
          nextStatus[k] = false;
        });
        onKeyStatusChange(nextStatus);
        setResults({});
        setExpandedKey(null);
        setInputValue('');
        success(
          'API Keys Cleared',
          result.message || 'All organization Data Source API keys have been cleared.',
        );
      } else {
        error('Clear Failed', result.message || 'Failed to clear API keys.');
      }
    } catch {
      error('Error', 'An error occurred while clearing API keys.');
    } finally {
      setBusy(null);
    }
  };

  const toggleExpand = (keyName: OrgKeyName) => {
    if (expandedKey === keyName) {
      setExpandedKey(null);
    } else {
      setExpandedKey(keyName);
      setInputValue('');
    }
  };

  const getStatusIcon = (keyName: OrgKeyName) => {
    const isConfig = configured[keyName];
    const res = results[keyName];

    if (res) {
      if (res.status === 'valid') return <i className="bi bi-shield-check" title={res.message} />;
      if (
        [
          'quota_exceeded',
          'credits_exhausted',
          'model_unavailable',
          'rate_limited',
          'timeout',
        ].includes(res.status)
      )
        return <i className="bi bi-shield-exclamation text-warning" title={res.message} />;
      return <i className="bi bi-shield-x text-danger" title={res.message} />;
    }

    if (isConfig) {
      return <i className="bi bi-check-lg" title="Configured" />;
    }
    return <i className="bi bi-dash" title="Not configured" />;
  };

  return (
    <>
      <section className="org-section settings-api-keys-section">
        <OrgSectionHeading
          plain
          title="Data Sources"
          sub="Optional — each enabled source adds more company discovery coverage."
          actions={
            <>
              <Button
                variant="danger"
                pill
                className="settings-clear-all-btn"
                onClick={clearAllKeys}
                disabled={busy !== null}
              >
                {busy === 'clear' ? 'Clearing...' : 'Clear All Keys'}
              </Button>
              <Button variant="primary" pill onClick={runTest} disabled={busy === 'test'}>
                {busy === 'test' ? 'Testing...' : 'Test All API Keys'}
              </Button>
            </>
          }
        />
        <div className="org-section-body">
          <div className="api-keys-tab">
            <div className="api-keys-groups">
              {KEY_GROUPS.filter((group) => group.category === 'data_source').map((group) => (
                <div key={group.title} className="api-keys-group">
                  <div className="api-keys-list">
                    {group.keys.map((keyName) => {
                      const meta = KEYS[keyName as OrgKeyName];
                      if (!meta) return null;
                      const kn = keyName as OrgKeyName;
                      const isExpanded = expandedKey === kn;

                      return (
                        <div
                          key={keyName}
                          className={`api-key-row-wrapper${isExpanded ? ' expanded' : ''}`}
                        >
                          <div className="api-key-row">
                            <div className="api-key-info">
                              <span
                                className={`api-key-status-icon ${configured[kn] ? 'status-configured' : 'status-empty'}`}
                              >
                                {getStatusIcon(kn)}
                              </span>
                              <div className="api-key-details">
                                <div className="api-key-header">
                                  <span className="api-key-name">{meta.label}</span>
                                  <div className="api-key-badges">
                                    {meta.required && (
                                      <span className="settings-badge settings-badge--required">
                                        REQUIRED
                                      </span>
                                    )}
                                    <span className="settings-badge settings-badge--app">
                                      ORG DEFAULT
                                    </span>
                                  </div>
                                  {meta.apolloInfo && (
                                    <button
                                      type="button"
                                      className="btn-unstyled settings-icon-btn"
                                      onClick={apolloModal.show}
                                      title="View required endpoints"
                                      aria-label="View required Apollo endpoints"
                                    >
                                      <i className="bi bi-info-circle" />
                                    </button>
                                  )}
                                </div>
                                <div className="api-key-status-text">
                                  {results[kn]?.message ? (
                                    <>
                                      <i
                                        className={`bi ${
                                          results[kn]?.status === 'valid'
                                            ? 'bi-check-circle api-key-status-ok'
                                            : 'bi-exclamation-circle api-key-status-warn'
                                        }`}
                                      />
                                      {results[kn]?.message}
                                    </>
                                  ) : configured[kn] ? (
                                    <>
                                      <i className="bi bi-check-circle api-key-status-ok" />
                                      Configured
                                    </>
                                  ) : (
                                    <>
                                      <i className="bi bi-dash-circle api-key-status-empty" />
                                      Not configured
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="api-key-actions">
                              <button
                                type="button"
                                className="btn-unstyled settings-icon-btn"
                                onClick={() => testSingleKey(kn)}
                                title="Test Current Key"
                                aria-label={`Test ${meta.label}`}
                                disabled={busy === 'test' || !configured[kn]}
                              >
                                <i className="bi bi-play-fill" />
                              </button>
                              <button
                                type="button"
                                className="btn-unstyled settings-icon-btn settings-icon-btn--danger"
                                onClick={() => clearKey(kn)}
                                title="Clear Key"
                                aria-label={`Clear ${meta.label}`}
                                disabled={busy === 'clear' || !configured[kn]}
                              >
                                <i className="bi bi-trash" />
                              </button>
                              <Button
                                variant="secondary"
                                size="sm"
                                pill
                                className="api-key-update-btn"
                                onClick={() => toggleExpand(kn)}
                              >
                                Update
                                <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
                              </Button>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="api-key-expand-content">
                              <input
                                ref={inputRef}
                                type="text"
                                className="api-key-input"
                                placeholder={`Paste your ${meta.label} here`}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                              />
                              <div className="api-key-expand-actions">
                                <Button
                                  variant="primary"
                                  pill
                                  onClick={() => updateKey(kn)}
                                  disabled={busy === 'update' || !inputValue.trim()}
                                >
                                  {busy === 'update' ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                  variant="secondary"
                                  pill
                                  disabled={busy === 'test' || !inputValue.trim()}
                                  onClick={() => testSingleKey(kn, inputValue.trim())}
                                >
                                  {busy === 'test' ? 'Testing...' : 'Test key'}
                                </Button>
                                <Button variant="secondary" pill onClick={() => toggleExpand(kn)}>
                                  Cancel
                                </Button>
                              </div>
                              <p className="api-key-expand-hint">
                                <i className="bi bi-info-circle" />
                                Organization default keys are used for member runs when allowed by
                                profile settings. They are stored encrypted and never logged.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Modal
        open={apolloModal.open}
        onClose={apolloModal.close}
        title="Required Apollo.io API Endpoints"
        size="md"
      >
        <p className="api-key-expand-hint">
          The following endpoints must be enabled when creating your Apollo.io API key:
        </p>
        <div className="settings-apollo-list">
          {APOLLO_ENDPOINTS.map((item, i) => (
            <div className="settings-apollo-item" key={item.endpoint}>
              <span>{i + 1}.</span>
              <code>{item.endpoint}</code>
              <span>— {item.description}</span>
            </div>
          ))}
        </div>
        <div className="settings-card__actions">
          <Button variant="primary" pill onClick={apolloModal.close}>
            Got it
          </Button>
        </div>
      </Modal>
    </>
  );
}
